import React, { useEffect, useState } from 'react';
import api from '../api';

const THEME = {
    card: '#1e293b', border: '#334155', textSec: '#94a3b8', 
    primary: '#3b82f6', success: '#10b981', danger: '#ef4444', warning: '#facc15',
    bg: '#0f172a'
};

const PeopleManager = () => {
    const [activeTab, setActiveTab] = useState('roster'); 
    const [loading, setLoading] = useState(true);
    const [isOwner, setIsOwner] = useState(false);
    
    // Data
    const [people, setPeople] = useState([]);
    const [qualTypes, setQualTypes] = useState([]);
    const [search, setSearch] = useState('');
    const [expandedRow, setExpandedRow] = useState(null);

    // Upload State
    const [uploadingFor, setUploadingFor] = useState(null); // { id: 'user-1', raw_id: 1, type: 'User' }
    const [newCert, setNewCert] = useState({ type: '', custom: '', file: null, expiry: '' });

    // Config State
    const [newType, setNewType] = useState('');

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const userRes = await api.get('users/me/');
            setIsOwner(userRes.data.is_superuser || (userRes.data.company_details?.type === 'OWNER'));

            const [pplRes, typesRes] = await Promise.all([
                api.get('people/roster/'),
                api.get('config/qualifications/')
            ]);
            
            setPeople(pplRes.data);
            setQualTypes(typesRes.data);
        } catch (err) { console.error(err); } 
        finally { setLoading(false); }
    };

    // --- ACTIONS ---

    const handleAddType = async (e) => {
        e.preventDefault();
        try {
            await api.post('config/qualifications/', { name: newType });
            setNewType('');
            const res = await api.get('config/qualifications/');
            setQualTypes(res.data);
        } catch(err) { alert("Failed to add type"); }
    };

    const handleDeleteType = async (id) => {
        if(!window.confirm("Remove this qualification type?")) return;
        try {
            await api.delete(`config/qualifications/${id}/`);
            setQualTypes(prev => prev.filter(t => t.id !== id));
        } catch(err) { alert("Failed to delete"); }
    };

    const handleUploadCert = async (e) => {
        e.preventDefault();
        const formData = new FormData();
        formData.append('target_type', uploadingFor.type); // 'User' or 'Worker'
        formData.append('target_id', uploadingFor.raw_id);
        
        if (newCert.type === 'OTHER') {
            formData.append('custom_name', newCert.custom);
        } else {
            formData.append('qualification_type', newCert.type);
        }
        
        formData.append('file', newCert.file);
        if (newCert.expiry) formData.append('expiry_date', newCert.expiry);

        try {
            await api.post('people/upload-cert/', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            alert("✅ Certificate Uploaded");
            setUploadingFor(null);
            setNewCert({ type: '', custom: '', file: null, expiry: '' });
            // Refresh Roster
            const res = await api.get('people/roster/');
            setPeople(res.data);
        } catch(err) { alert("Upload failed"); }
    };

    // --- RENDER HELPERS ---
    const filteredPeople = people.filter(p => 
        p.name.toLowerCase().includes(search.toLowerCase()) || 
        p.company.toLowerCase().includes(search.toLowerCase()) ||
        p.role.toLowerCase().includes(search.toLowerCase())
    );

    const toggleRow = (id) => setExpandedRow(expandedRow === id ? null : id);

    if (loading) return <div style={{color:'white', padding:'20px'}}>Loading People...</div>;

    return (
        <div className="animate-fade-in" style={{padding:'20px'}}>
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px'}}>
                <h2 style={{color:'white', margin:0}}>👥 People Manager</h2>
                {activeTab === 'roster' && (
                    <input 
                        placeholder="Search names, companies..." 
                        value={search} onChange={e => setSearch(e.target.value)}
                        style={{padding:'10px', borderRadius:'6px', border:`1px solid ${THEME.border}`, background: THEME.card, color:'white', width:'300px'}}
                    />
                )}
            </div>

            <div style={{display: 'flex', gap: '10px', marginBottom: '25px', background:'#020617', padding:'5px', borderRadius:'8px', border:`1px solid ${THEME.border}`, width: 'fit-content'}}>
                <button onClick={() => setActiveTab('roster')} style={{padding: '8px 20px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize:'0.9rem', fontWeight:600, background: activeTab === 'roster' ? THEME.primary : 'transparent', color: activeTab === 'roster' ? 'white' : THEME.textSec}}>All People</button>
                {isOwner && <button onClick={() => setActiveTab('config')} style={{padding: '8px 20px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize:'0.9rem', fontWeight:600, background: activeTab === 'config' ? THEME.primary : 'transparent', color: activeTab === 'config' ? 'white' : THEME.textSec}}>Configuration</button>}
            </div>

            {/* TAB 1: ROSTER */}
            {activeTab === 'roster' && (
                <div style={{background: THEME.card, borderRadius:'12px', border:`1px solid ${THEME.border}`, overflowX:'auto'}}>
                    <table style={{width:'100%', borderCollapse:'collapse', color:'white', fontSize:'0.9rem'}}>
                        <thead style={{background:'#0f172a', borderBottom:`1px solid ${THEME.border}`}}>
                            <tr>
                                <th style={{padding:'15px', textAlign:'left'}}>Name</th>
                                <th style={{padding:'15px', textAlign:'left'}}>Role</th>
                                <th style={{padding:'15px', textAlign:'left'}}>Company</th>
                                <th style={{padding:'15px', textAlign:'center'}}>Type</th>
                                <th style={{padding:'15px', textAlign:'center'}}>Inducted</th>
                                <th style={{padding:'15px', textAlign:'center'}}>Certs</th>
                                <th style={{padding:'15px', textAlign:'right'}}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredPeople.map(p => (
                                <React.Fragment key={p.id}>
                                    <tr style={{borderBottom:`1px solid ${THEME.border}`, background: expandedRow === p.id ? '#1e293b' : 'transparent'}}>
                                        <td style={{padding:'15px', fontWeight:'bold'}}>{p.name}</td>
                                        <td style={{padding:'15px', color:THEME.textSec}}>{p.role}</td>
                                        <td style={{padding:'15px'}}>{p.company}</td>
                                        <td style={{padding:'15px', textAlign:'center'}}><span style={{fontSize:'0.75rem', padding:'2px 8px', borderRadius:'4px', background:'rgba(255,255,255,0.1)'}}>{p.type}</span></td>
                                        <td style={{padding:'15px', textAlign:'center'}}>{p.is_inducted ? '✅' : '❌'}</td>
                                        <td style={{padding:'15px', textAlign:'center', fontWeight:'bold', color: p.qualifications.length > 0 ? THEME.success : THEME.textSec}}>
                                            {p.qualifications.length}
                                        </td>
                                        <td style={{padding:'15px', textAlign:'right'}}>
                                            <button onClick={() => toggleRow(p.id)} style={{background:'transparent', border:`1px solid ${THEME.primary}`, color:THEME.primary, padding:'5px 10px', borderRadius:'4px', cursor:'pointer'}}>
                                                {expandedRow === p.id ? 'Hide' : 'View'}
                                            </button>
                                        </td>
                                    </tr>
                                    {expandedRow === p.id && (
                                        <tr style={{background:'#0f172a', borderBottom:`1px solid ${THEME.border}`}}>
                                            <td colSpan="7" style={{padding:'20px'}}>
                                                <div style={{display:'flex', justifyContent:'space-between', marginBottom:'10px'}}>
                                                    <h4 style={{margin:0, color:'white'}}>Qualifications & Certificates</h4>
                                                    <button onClick={() => { setUploadingFor(p); setNewCert({ ...newCert, type: qualTypes[0]?.id || '' }); }} style={{background:THEME.primary, color:'white', border:'none', padding:'6px 12px', borderRadius:'6px', cursor:'pointer'}}>+ Add Cert</button>
                                                </div>
                                                
                                                <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(200px, 1fr))', gap:'15px'}}>
                                                    {p.qualifications.length === 0 && <span style={{color:THEME.textSec, fontStyle:'italic'}}>No qualifications on record.</span>}
                                                    {p.qualifications.map(q => (
                                                        <div key={q.id} style={{background:THEME.card, padding:'10px', borderRadius:'6px', border:`1px solid ${THEME.border}`}}>
                                                            <div style={{fontWeight:'bold', color:'white', marginBottom:'5px'}}>{q.name}</div>
                                                            <div style={{fontSize:'0.8rem', color:THEME.textSec}}>Expires: <span style={{color: new Date(q.expiry) < new Date() ? THEME.danger : 'white'}}>{q.expiry || 'N/A'}</span></div>
                                                            {q.file && (
                                                                <a href={q.file} target="_blank" rel="noreferrer" style={{display:'block', marginTop:'8px', fontSize:'0.8rem', color:THEME.primary}}>View Document</a>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* TAB 2: CONFIGURATION (Owner Only) */}
            {activeTab === 'config' && isOwner && (
                <div style={{maxWidth:'600px'}}>
                    <div style={{background: THEME.card, padding:'20px', borderRadius:'12px', border:`1px solid ${THEME.border}`, marginBottom:'20px'}}>
                        <h4 style={{marginTop:0, color:'white'}}>Add Qualification Type</h4>
                        <form onSubmit={handleAddType} style={{display:'flex', gap:'10px'}}>
                            <input 
                                placeholder="e.g. Forklift License" 
                                value={newType} 
                                onChange={e => setNewType(e.target.value)} 
                                style={{flex:1, padding:'10px', borderRadius:'6px', border:`1px solid ${THEME.border}`, background:'#020617', color:'white'}} 
                                required 
                            />
                            <button type="submit" style={{background:THEME.success, color:'white', border:'none', padding:'0 20px', borderRadius:'6px', fontWeight:'bold', cursor:'pointer'}}>Add</button>
                        </form>
                    </div>

                    <div style={{display:'flex', flexDirection:'column', gap:'10px'}}>
                        {qualTypes.map(t => (
                            <div key={t.id} style={{padding:'15px', background:THEME.card, borderRadius:'8px', border:`1px solid ${THEME.border}`, display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                                <span style={{color:'white', fontWeight:500}}>{t.name}</span>
                                <button onClick={() => handleDeleteType(t.id)} style={{background:'transparent', border:'none', color:THEME.textSec, cursor:'pointer'}}>🗑️</button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* MODAL: UPLOAD CERT */}
            {uploadingFor && (
                <div style={{position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.8)', display:'flex', justifyContent:'center', alignItems:'center', zIndex:100}}>
                    <div style={{background: THEME.card, padding:'25px', borderRadius:'12px', width:'400px', border:`1px solid ${THEME.border}`}}>
                        <h4 style={{marginTop:0, color:'white'}}>Add Qualification for {uploadingFor.name}</h4>
                        <form onSubmit={handleUploadCert}>
                            <label style={{display:'block', color:THEME.textSec, fontSize:'0.8rem', marginBottom:'5px'}}>Type</label>
                            <select 
                                value={newCert.type} 
                                onChange={e => setNewCert({...newCert, type: e.target.value})} 
                                style={{width:'100%', padding:'10px', borderRadius:'6px', border:`1px solid ${THEME.border}`, background:'#020617', color:'white', marginBottom:'10px'}}
                            >
                                {qualTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                <option value="OTHER">Other (Custom)</option>
                            </select>

                            {newCert.type === 'OTHER' && (
                                <input 
                                    placeholder="Custom Name" 
                                    value={newCert.custom} 
                                    onChange={e => setNewCert({...newCert, custom: e.target.value})} 
                                    style={{width:'100%', padding:'10px', borderRadius:'6px', border:`1px solid ${THEME.border}`, background:'#020617', color:'white', marginBottom:'10px', boxSizing:'border-box'}} 
                                    required 
                                />
                            )}

                            <label style={{display:'block', color:THEME.textSec, fontSize:'0.8rem', marginBottom:'5px'}}>Expiry Date</label>
                            <input 
                                type="date" 
                                value={newCert.expiry} 
                                onChange={e => setNewCert({...newCert, expiry: e.target.value})} 
                                style={{width:'100%', padding:'10px', borderRadius:'6px', border:`1px solid ${THEME.border}`, background:'#020617', color:'white', marginBottom:'10px', boxSizing:'border-box'}} 
                            />

                            <label style={{display:'block', color:THEME.textSec, fontSize:'0.8rem', marginBottom:'5px'}}>Document</label>
                            <input 
                                type="file" 
                                onChange={e => setNewCert({...newCert, file: e.target.files[0]})} 
                                style={{width:'100%', padding:'10px', borderRadius:'6px', border:`1px solid ${THEME.border}`, background:'#020617', color:'white', marginBottom:'20px', boxSizing:'border-box'}} 
                                required 
                            />

                            <div style={{display:'flex', gap:'10px'}}>
                                <button type="button" onClick={() => setUploadingFor(null)} style={{flex:1, padding:'10px', background:'transparent', border:`1px solid ${THEME.border}`, color:'white', borderRadius:'6px', cursor:'pointer'}}>Cancel</button>
                                <button type="submit" style={{flex:1, padding:'10px', background:THEME.success, border:'none', color:'white', borderRadius:'6px', cursor:'pointer', fontWeight:'bold'}}>Upload</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PeopleManager;